import { useEffect, useMemo, useRef, useState, type ChangeEvent, type FormEvent } from "react";
import { Link, useLocation, useParams, useSearchParams } from "react-router-dom";
import SEO from "../components/SEO";
import { getFormsPageSeo } from "../../../shared/formsSeo";
import "./forms.css";
import {
  CONSENT_VERSION, FORM_DEFINITIONS, FORM_TYPES, LOA_CERTIFICATION,
  SIGNATURE_CONSENT, VALIDATION_CERTIFICATION, CONTROLLED_CERTIFICATION, attachmentLabel, formSchema,
  visibleField, requiredLicenseUploads, formTemplateVersion, currentFormDate, type FormField, type OnboardingFormType,
} from "../../../shared/onboardingForms";
import PaymentInformationPreview from "./PaymentInformationPreview";
import PaymentInformationForm from "./PaymentInformationForm";

type FormConfig = { available: boolean; unavailableReason: string | null; adminTesting?: boolean; paymentInformationPreview?: boolean };
type Files = Record<string, File | null>;
type FieldErrors = Record<string, string>;
const allowedMime = ["application/pdf", "image/jpeg", "image/png"];
const MAX_FILE_SIZE = 5 * 1024 * 1024;

function makeKey() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, c => {
    const r = Math.random() * 16 | 0;
    return (c === "x" ? r : (r & 3 | 8)).toString(16);
  });
}

function isFormType(value: string | undefined): value is OnboardingFormType {
  return !!value && (FORM_TYPES as readonly string[]).includes(value);
}

function useFormConfig() {
  const [config, setConfig] = useState<FormConfig | null>(null);
  const [error, setError] = useState("");
  const load = () => {
    setError("");
    fetch("/api/forms/config", { credentials: "same-origin", cache: "no-store" })
      .then(async response => {
        if (!response.ok) throw new Error("We could not check form availability.");
        setConfig(await response.json() as FormConfig);
      })
      .catch(() => setError("Availability could not be checked. Please try again before submitting."));
  };
  useEffect(load, []);
  return { config, error, reload: load };
}

function AdminTestingNotice({ config }: { config: FormConfig | null }) {
  if (!config?.adminTesting) return null;
  return <div className="forms-alert forms-alert-warning" role="status">
    <strong>Administrator testing only.</strong> Public submissions remain disabled.
    Use clearly labeled synthetic information and test license copies. Test submissions
    use the normal PDF, secure storage, and email workflow.
  </div>;
}

function FormPicker({ config, configError, retry }: { config: FormConfig | null; configError: string; retry: () => void }) {
  return (
    <div className="forms-shell">
      <div className="forms-topline"><span>Premier Pharmaceuticals</span><span>Practice onboarding</span></div>
      <header className="forms-intro">
        <p className="forms-eyebrow">Forms portal</p>
        <h1>Paperwork, <em>made clear.</em></h1>
        <p>Choose the form that fits your practice. You can submit official paperwork here without creating an account.</p>
      </header>
      <AdminTestingNotice config={config} />
      {configError && <div className="forms-alert forms-alert-warning" role="status">{configError} <button type="button" onClick={retry}>Check again</button></div>}
      {config && !config.available && <div className="forms-alert forms-alert-warning" role="status">{config.unavailableReason || "Online submissions are temporarily unavailable."} Forms remain available to preview, but cannot be submitted right now.</div>}
      <div className="forms-picker">
        {FORM_TYPES.map((type, index) => (
          <Link className="forms-choice" key={type} to={`/forms/${type}`}>
            <span className="forms-choice-number">0{index + 1}</span>
            <span className="forms-choice-copy"><strong>{FORM_DEFINITIONS[type].title}</strong><small>{type === "payment-information" ? "Submit signed credit-card or ACH instructions for manual processing. Alternatively, payment can be processed at checkout for online orders." : FORM_DEFINITIONS[type].description}</small></span>
            <span className="forms-arrow">Complete Form <span aria-hidden="true">↗</span></span>
          </Link>
        ))}
      </div>
      <p className="forms-privacy-note">Your information and documents are transmitted securely for onboarding review. This portal does not change how you place orders.</p>
    </div>
  );
}

function UploadField({ kind, required, file, error, onChange }: { kind: string; required: boolean; file: File | null; error?: string; onChange: (file: File | null) => void }) {
  const inputId = `upload-${kind}`;
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <div className={`forms-upload ${error ? "has-error" : ""}`}>
      <label htmlFor={inputId} className="forms-label">{attachmentLabel(kind)} copy {required && <span className="forms-required">Required</span>}</label>
      <div className="forms-upload-row">
        <input ref={inputRef} id={inputId} type="file" required={required} aria-required={required} aria-invalid={!!error} accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png" onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event.target.files?.[0] ?? null)} aria-describedby={`${inputId}-help${error ? ` ${inputId}-error` : ""}`} />
        {file && <button className="forms-text-button" type="button" onClick={() => {
          if (inputRef.current) inputRef.current.value = "";
          onChange(null);
        }}>Remove</button>}
      </div>
      <p id={`${inputId}-help`} className="forms-hint">PDF, JPG, or PNG · maximum 5 MB</p>
      {file && <p className="forms-file-name">{file.name} <span>({(file.size / 1024 / 1024).toFixed(2)} MB)</span></p>}
      {error && <p id={`${inputId}-error`} className="forms-error">{error}</p>}
    </div>
  );
}

function FieldControl({ field, value, error, onChange }: { field: FormField; value: string; error?: string; onChange: (value: string) => void }) {
  const id = `field-${field.name}`;
  const common = {
    id, name: field.name, value, onChange: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => onChange(event.target.value),
    "aria-invalid": !!error, "aria-describedby": error ? `${id}-error` : undefined,
    className: `forms-control ${error ? "is-invalid" : ""}`,
    maxLength: field.max,
  };
  return (
    <div className={`forms-field ${field.type === "textarea" ? "forms-field-wide" : ""}`}>
      <label className="forms-label" htmlFor={id}>{field.label}{!field.optional && <span className="forms-required">Required</span>}</label>
      {field.type === "select" ? (
        <select {...common}><option value="">Choose an option</option>{field.options?.map(option => <option value={option} key={option}>{option}</option>)}</select>
      ) : field.type === "textarea" ? (
        <textarea {...common} rows={4} />
      ) : (
        <input {...common} type={field.type || "text"} min={field.name === "deaLicenseExpiration" ? currentFormDate() : undefined} aria-required={!field.optional} autoComplete={field.type === "email" ? "email" : "off"} />
      )}
      {error && <p id={`${id}-error`} className="forms-error">{error}</p>}
    </div>
  );
}

function SubmissionForm({ type, config, configError, retryConfig }: { type: OnboardingFormType; config: FormConfig | null; configError: string; retryConfig: () => void }) {
  const definition = FORM_DEFINITIONS[type];
  const [data, setData] = useState<Record<string, string>>(() => Object.fromEntries(definition.fields.map(field => [field.name, ""])));
  const [files, setFiles] = useState<Files>({ stateLicense: null, deaLicense: null });
  const [signerName, setSignerName] = useState("");
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [message, setMessage] = useState("");
  const [reference, setReference] = useState("");
  const [status, setStatus] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [idempotencyKey, setIdempotencyKey] = useState("");
  const [attemptSnapshot, setAttemptSnapshot] = useState("");
  const [immutableConflict, setImmutableConflict] = useState(false);
  const sections = useMemo(() => [...new Set(definition.fields.map(field => field.section))], [definition.fields]);
  const isAvailable = !!config?.available && !configError;

  const updateField = (name: string, value: string) => {
    if(type === "loa" && name === "stateLicensePreviouslySubmitted" && value === "Yes") {
      setFiles(previous => ({...previous,stateLicense:null}));
      setErrors(previous => {
        const next = {...previous};
        delete next["files.stateLicense"]; delete next.stateLicense; delete next.files;
        return next;
      });
    }
    setData(previous => {
      const next = { ...previous, [name]: value };
      for (const field of definition.fields) if (field.when && field.when.field === name && value !== field.when.value) next[field.name] = "";
      return next;
    });
    setErrors(previous => { const next = { ...previous }; delete next[`data.${name}`]; return next; });
  };

  const getSnapshot = (snapshotData = data) => JSON.stringify({
    data: snapshotData, signerName: definition.signed ? signerName.trim() : "", consent: definition.signed ? consent : false,
    files: Object.fromEntries(requiredLicenseUploads(type, snapshotData, formTemplateVersion(type)).map(({ kind }) => {
      const file = files[kind];
      return [kind, file ? [file.name, file.size, file.type, file.lastModified] : null];
    })),
  });

  const validate = () => {
    const nextErrors: FieldErrors = {};
    const cleanData = { ...data };
    definition.fields.forEach(field => { if (!visibleField(field, cleanData)) cleanData[field.name] = ""; });
    const parsed = formSchema(type).safeParse({ data: cleanData, signerName: definition.signed ? signerName.trim() : "", consent: definition.signed ? consent : false, templateVersion: formTemplateVersion(type) });
    if (!parsed.success) {
      parsed.error.issues.forEach(issue => {
        const key = issue.path.join(".");
        if (!nextErrors[key]) nextErrors[key] = issue.message;
      });
    }
    for (const { kind } of requiredLicenseUploads(type, cleanData, formTemplateVersion(type))) {
      const file = files[kind];
      if (!file) nextErrors[`files.${kind}`] = "Attach the required license copy.";
      else if (file && (!allowedMime.includes(file.type) || !/\.(pdf|jpe?g|png)$/i.test(file.name))) nextErrors[`files.${kind}`] = "Choose a PDF, JPG, or PNG file.";
      else if (file && file.size > MAX_FILE_SIZE) nextErrors[`files.${kind}`] = "File must be 5 MB or smaller.";
    }
    setErrors(nextErrors);
    setData(cleanData);
    return { cleanData, valid: Object.keys(nextErrors).length === 0 };
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setMessage("");
    setReference("");
    setStatus("");
    const { cleanData, valid } = validate();
    if (!valid) {
      setMessage("Review the highlighted fields and try again.");
      document.querySelector(".forms-error")?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    const currentSnapshot = getSnapshot(cleanData);
    if (idempotencyKey && attemptSnapshot && currentSnapshot !== attemptSnapshot) {
      setImmutableConflict(true);
      setMessage("This submission has already been attempted with different answers. Start a new submission to send the updated information.");
      return;
    }
    const key = idempotencyKey || makeKey();
    if (!idempotencyKey) {
      setIdempotencyKey(key);
      setAttemptSnapshot(currentSnapshot);
    }
    setImmutableConflict(false);
    setSubmitting(true);
    setErrors({});
    try {
      const body = new FormData();
      body.append("payload", JSON.stringify({ data: cleanData, signerName: definition.signed ? signerName.trim() : "", consent: definition.signed ? consent : false, templateVersion: formTemplateVersion(type) }));
      body.append("idempotencyKey", key);
      for (const { kind } of requiredLicenseUploads(type, cleanData, formTemplateVersion(type))) {
        if (files[kind]) body.append(kind, files[kind] as File);
      }
      const response = await fetch(`/api/forms/${type}/submissions`, { method: "POST", body });
      const result = await response.json().catch(() => ({}));
      if (response.status === 201 || response.status === 200) {
        if (result.status !== "ready" || !result.reference) throw new Error(result.error || "The server did not confirm receipt. Your answers are still here; retry safely.");
        setReference(result.reference);
        setStatus("ready");
        setMessage("");
        setSignerName("");
        setConsent(false);
        setIdempotencyKey("");
        setAttemptSnapshot("");
        setFiles({ stateLicense: null, deaLicense: null });
        return;
      }
      if (response.status === 202) {
        setReference(result.reference || "");
        setStatus(result.status || "processing");
        setMessage(result.error || "The submission is retained but is not complete yet. Retry when you are ready; the same safe submission key will be used.");
        return;
      }
      if (response.status === 400 && result.fieldErrors) {
        setErrors(result.fieldErrors as FieldErrors);
        setMessage(result.error || "Some information needs attention.");
        setIdempotencyKey("");
        setAttemptSnapshot("");
        return;
      }
      if (response.status === 409) {
        setImmutableConflict(true);
        setMessage(result.error || "The server has a different submission recorded for this key. Start a new submission; the existing submission was not changed.");
        return;
      }
      if (response.status === 503) throw new Error(result.error || "Submissions are temporarily unavailable. Your answers have been kept.");
      throw new Error(result.error || "We could not submit the form. Your answers have been kept.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Submission failed. Your answers have been kept.");
    } finally {
      setSubmitting(false);
    }
  };

  const startNewAttempt = () => {
    setIdempotencyKey("");
    setAttemptSnapshot("");
    setImmutableConflict(false);
    setMessage("");
    setReference("");
    setStatus("");
  };
  const clearSignature = () => { setSignerName(""); setConsent(false); setErrors(prev => { const next = { ...prev }; delete next.signerName; delete next.consent; return next; }); };
  const renderUploads = (fieldName: string) => {
    if(type === "loa" && fieldName === "stateLicensePreviouslySubmitted" && data.stateLicensePreviouslySubmitted === "Yes") {
      return <p className="forms-hint">No new upload is required because you indicated that a copy of this state license has already been submitted. Premier will still review the license separately.</p>;
    }
    const uploads = requiredLicenseUploads(type, data, formTemplateVersion(type)).filter(upload => upload.afterField === fieldName);
    if (!uploads.length) return null;
    return (
      <div className="forms-upload-grid">
        {uploads.map(({ kind }) => (
          <UploadField key={kind} kind={kind} required file={files[kind] || null} error={errors[`files.${kind}`] || errors[kind] || errors.files} onChange={file => { setFiles(previous => ({ ...previous, [kind]: file })); setErrors(previous => { const next = { ...previous }; delete next[`files.${kind}`]; delete next[kind]; delete next.files; return next; }); }} />
        ))}
      </div>
    );
  };

  if (status === "ready" && reference) {
    return (
      <div className="forms-shell">
        <div className="forms-topline"><span>Premier Pharmaceuticals</span><span>Practice onboarding</span></div>
        <section className="forms-confirmation" aria-live="polite">
          <div className="forms-confirmation-mark" aria-hidden="true" />
          <p className="forms-eyebrow">Submission received</p>
          <h1>Thank you. <em>We have it.</em></h1>
          <p>Your form was received. Keep this reference for your records.</p>
          <div className="forms-reference"><span>Reference</span><strong>{reference}</strong></div>
          <p className="forms-smallprint">Receipt confirms submission only. It is not an approval or authorization for an account, order, or shipment.</p>
          <Link to="/forms" className="forms-button forms-button-secondary">Return to forms</Link>
        </section>
      </div>
    );
  }

  return (
    <div className="forms-shell">
      <div className="forms-topline"><span>Premier Pharmaceuticals</span><span>Practice onboarding</span></div>
      <div className="forms-form-head">
        <Link to="/forms" className="forms-back">← All forms</Link>
        <p className="forms-eyebrow">Official paperwork</p>
        <h1>{definition.title}</h1>
        <p>{definition.description}</p>
      </div>
      <div className="forms-instructions"><span className="forms-instruction-icon" aria-hidden="true">i</span><p>{definition.instructions}</p></div>
      {configError && <div className="forms-alert forms-alert-warning" role="status">{configError} <button type="button" onClick={retryConfig}>Check again</button></div>}
      <AdminTestingNotice config={config} />
      {config && !config.available && <div className="forms-alert forms-alert-warning" role="status">{config.unavailableReason || "Online submissions are temporarily unavailable."} You may review this form, but submission is disabled.</div>}
      {!config && !configError && <div className="forms-config-loading" aria-label="Checking submission availability"><span /> Checking secure submission availability</div>}
      {message && <div className={`forms-alert ${status === "failed" || status === "processing" || immutableConflict ? "forms-alert-warning" : "forms-alert-error"}`} role="alert">{message}{reference && status !== "ready" && <span className="forms-alert-reference"> Reference: {reference}</span>}{immutableConflict && <button type="button" onClick={startNewAttempt}>Start a new submission</button>}</div>}
      <form className="forms-form" onSubmit={handleSubmit} noValidate>
        {sections.map((section, index) => (
          <fieldset className="forms-section" key={section}>
            <legend><span>{String(index + 1).padStart(2, "0")}</span>{section}</legend>
            <div className={`forms-fields${type === "controlled-substance" && section === "Customer information" ? " forms-fields-dea" : ""}`}>
              {definition.fields.filter(field => field.section === section && visibleField(field, data)).map(field => (
                <div key={field.name} className={field.type === "textarea" ? "forms-field-wide" : undefined}>
                  <FieldControl field={field} value={data[field.name] || ""} error={errors[`data.${field.name}`] || errors[field.name]} onChange={value => updateField(field.name, value)} />
                  {renderUploads(field.name)}
                </div>
              ))}
            </div>
          </fieldset>
        ))}
        {definition.signed && (
          <fieldset className="forms-section forms-signing">
            <legend><span>S</span>Certification &amp; signature</legend>
            <p className="forms-certification">{type === "loa" ? LOA_CERTIFICATION : type === "controlled-substance" ? CONTROLLED_CERTIFICATION : VALIDATION_CERTIFICATION}</p>
            <label className="forms-label" htmlFor="signer-name">Typed full name <span className="forms-required">Required</span></label>
            <input id="signer-name" className={`forms-control forms-signature-input ${errors.signerName ? "is-invalid" : ""}`} value={signerName} onChange={event => { setSignerName(event.target.value); setErrors(previous => { const next = { ...previous }; delete next.signerName; return next; }); }} maxLength={140} autoComplete="name" aria-invalid={!!errors.signerName} aria-describedby={`signer-name-help${errors.signerName ? " signer-name-error" : ""}`} />
            <div className="forms-signature-actions"><span id="signer-name-help" className="forms-hint">{type === "loa" ? "Must match the physician name entered above." : "Your typed name will be recorded as your electronic signature."}</span><button type="button" className="forms-text-button" onClick={clearSignature}>Clear signature</button></div>
            {errors.signerName && <p id="signer-name-error" className="forms-error">{errors.signerName}</p>}
            <label className={`forms-consent ${errors.consent ? "has-error" : ""}`}>
              <input type="checkbox" checked={consent} aria-invalid={!!errors.consent} aria-describedby={errors.consent ? "signature-consent-error" : undefined} onChange={event => { setConsent(event.target.checked); setErrors(previous => { const next = { ...previous }; delete next.consent; return next; }); }} />
              <span>{SIGNATURE_CONSENT}</span>
            </label>
            {errors.consent && <p id="signature-consent-error" className="forms-error">{errors.consent}</p>}
            <p className="forms-hint">Electronic signature record version: {CONSENT_VERSION}</p>
          </fieldset>
        )}
        {errors.form && <div className="forms-alert forms-alert-error">{errors.form}</div>}
        <div className="forms-submit-row">
          <p>Fields marked required must be completed. Your answers are not saved in this browser.</p>
          <button className="forms-button" type="submit" disabled={!isAvailable || submitting}>
            {submitting ? "Sending securely…" : status && status !== "ready" ? "Retry submission" : "Submit form"}
          </button>
        </div>
      </form>
      <footer className="forms-footer"><span>Premier Pharmaceuticals</span><span>Questions? Contact our onboarding team.</span></footer>
    </div>
  );
}

export default function Forms() {
  const { pathname } = useLocation();
  const seo = getFormsPageSeo(pathname);
  return <>
    <SEO
      title={seo?.title ?? "Form Not Found"}
      description={seo?.description ?? "The requested onboarding form could not be found."}
      path={seo?.path ?? pathname}
      noindex
      follow
    />
    <FormsContent />
  </>;
}

function FormsContent() {
  const { formType } = useParams<{ formType: string }>();
  const [searchParams] = useSearchParams();
  const { config, error, reload } = useFormConfig();
  if (!formType) return <FormPicker config={config} configError={error} retry={reload} />;
  if (formType === "payment-information") {
    if (searchParams.get("preview") === "1" && config?.paymentInformationPreview) return <PaymentInformationPreview />;
    return <PaymentInformationForm config={config} configError={error} retryConfig={reload} />;
  }
  if (!isFormType(formType)) return <div className="forms-shell"><div className="forms-topline"><span>Premier Pharmaceuticals</span><span>Practice onboarding</span></div><div className="forms-not-found"><p className="forms-eyebrow">Form not found</p><h1>That form isn’t here.</h1><Link className="forms-button" to="/forms">Browse available forms</Link></div></div>;
  return <SubmissionForm key={formType} type={formType} config={config} configError={error} retryConfig={reload} />;
}
